import React, { useState } from 'react';
import { Row, Col, Card, Table, Badge, Form, Button } from 'react-bootstrap';
import { 
  BarChart, Bar, PieChart, Pie, LineChart, Line, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import { FaFileDownload, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';

// Utils
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const getStatusBadgeVariant = (status) => {
  switch (status) {
    case 'Completed':
      return 'success';
    case 'In Progress':
      return 'primary';
    case 'On Hold':
      return 'warning';
    case 'Proposal Sent':
      return 'info';
    case 'Payment Schedule Sent':
      return 'secondary';
    case 'New':
      return 'dark';
    default:
      return 'secondary';
  }
};

// Color palette for charts
const COLORS = [
  '#3366CC', '#DC3912', '#FF9900', '#109618', '#990099', 
  '#0099C6', '#DD4477', '#66AA00', '#B82E2E', '#316395'
];

const ProjectsReport = ({ data, viewMode = 'both' }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [searchTerm, setSearchTerm] = useState('');
  
  if (!data || !data.data) {
    return <div>No data available</div>;
  }
  
  const reportType = data.type;
  let reportData = data.data;
  
  // Normalize the data structure based on report type
  // Backend returns different structures: {details, summary} or {projects, summary}
  if (reportType === 'projects-by-date' && reportData.projects) {
    // Projects by date returns {projects: [...], summary: {...}}
    // Extract just the projects array for this report type
    reportData = reportData.projects;
  }
  // For projects-by-status, keep the full object with {details, summary}
  
  // Calculate summary statistics
  const calculateSummary = () => {
    if (reportType === 'projects-by-status') {
      const totalProjects = reportData.summary.reduce((sum, item) => sum + item.Count, 0);
      const totalValue = reportData.summary.reduce((sum, item) => sum + item.TotalValue, 0);
      
      return {
        totalProjects,
        totalValue,
        averageValue: totalProjects > 0 ? totalValue / totalProjects : 0,
        statusCount: reportData.summary.length
      };
    } else if (reportType === 'projects-by-date') {
      const totalProjects = reportData.length || 0;
      const totalValue = reportData.reduce((sum, item) => sum + (item.TotalCost || 0), 0);
      
      return {
        totalProjects,
        totalValue,
        averageValue: totalProjects > 0 ? totalValue / totalProjects : 0,
        completedProjects: reportData.filter(p => p.Status === 'Completed').length
      };
    } else if (reportType === 'project-durations') {
      const projects = reportData.projects || [];
      const totalProjects = projects.length;
      const projectsWithDuration = projects.filter(p => p.DurationDays != null);
      const totalDurationDays = projectsWithDuration.reduce((sum, p) => sum + p.DurationDays, 0);
      
      return {
        totalProjects,
        averageDuration: projectsWithDuration.length > 0 ? totalDurationDays / projectsWithDuration.length : 0,
        longestDuration: Math.max(...projectsWithDuration.map(p => p.DurationDays), 0),
        shortestDuration: Math.min(...projectsWithDuration.map(p => p.DurationDays), 0)
      };
    }
    
    return {};
  };
  
  const summary = calculateSummary();
  
  // Sort table data
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };
  
  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return <FaSort className="ms-1 text-muted" />;
    }
    return sortConfig.direction === 'ascending' ? 
      <FaSortUp className="ms-1" /> : <FaSortDown className="ms-1" />;
  };
  
  const getSortedData = (items) => {
    if (!items || !sortConfig.key) return items;
    
    const sortableItems = [...items];
    sortableItems.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Handle nested properties or special cases
      if (sortConfig.key === 'ClientName' && reportType === 'projects-by-status') {
        aValue = a['ClientName'];
        bValue = b['ClientName'];
      }
      
      // Handle null values
      if (aValue === null) return sortConfig.direction === 'ascending' ? -1 : 1;
      if (bValue === null) return sortConfig.direction === 'ascending' ? 1 : -1;
      
      // For strings, ignore case
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      // Dates need special handling
      if (sortConfig.key === 'CreatedAt' || sortConfig.key === 'StartDate' || sortConfig.key === 'EndDate') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }
      
      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
    
    return sortableItems;
  };
  
  // Filter table data
  const getFilteredData = (items) => {
    if (!items || !searchTerm) return items;
    
    return items.filter(item => {
      // Search through all string and number fields
      return Object.keys(item).some(key => {
        const value = item[key];
        if (value === null || value === undefined) return false;
        
        if (typeof value === 'string') {
          return value.toLowerCase().includes(searchTerm.toLowerCase());
        } else if (typeof value === 'number') {
          return value.toString().includes(searchTerm);
        }
        
        return false;
      });
    });
  };
  
  // Render project status report
  const renderProjectsByStatus = () => {
    // Prepare chart data for pie chart
    const chartData = reportData.summary.map((item, index) => ({
      name: item.Status,
      value: item.Count,
      color: COLORS[index % COLORS.length]
    }));
    
    // Prepare chart data for bar chart
    const valueChartData = reportData.summary.map((item, index) => ({
      name: item.Status,
      value: item.TotalValue,
      color: COLORS[index % COLORS.length]
    }));
    
    // Filter and sort project details
    let projectDetails = reportData.details;
    if (searchTerm) {
      projectDetails = getFilteredData(projectDetails);
    }
    if (sortConfig.key) {
      projectDetails = getSortedData(projectDetails);
    }
    
    const renderCharts = () => (
      <>
        <Row className="mb-4">
          <Col md={6}>
            <Card>
              <Card.Body>
                <h5 className="text-center mb-3">Projects by Status</h5>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      fill="#8884d8"
                      paddingAngle={5}
                      dataKey="value"
                      nameKey="name"
                      label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value} Projects`, 'Count']} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={6}>
            <Card>
              <Card.Body>
                <h5 className="text-center mb-3">Project Value by Status</h5>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={valueChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis
                      tickFormatter={(value) => `$${Math.floor(value / 1000)}k`}
                    />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), 'Value']}
                    />
                    <Legend />
                    <Bar dataKey="value" fill="#8884d8">
                      {valueChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </>
    );
    
    const renderSummaryTable = () => (
      <Card className="mb-4">
        <Card.Body>
          <h5 className="mb-3">Summary by Status</h5>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Status</th>
                <th>Project Count</th>
                <th>Total Value</th>
                <th>Average Value</th>
                <th>% of Total</th>
              </tr>
            </thead>
            <tbody>
              {reportData.summary.map((item, index) => (
                <tr key={index}>
                  <td>
                    <Badge bg={getStatusBadgeVariant(item.Status)}>
                      {item.Status}
                    </Badge>
                  </td>
                  <td>{item.Count}</td>
                  <td>{formatCurrency(item.TotalValue)}</td>
                  <td>{formatCurrency(item.TotalValue / item.Count)}</td>
                  <td>{((item.Count / summary.totalProjects) * 100).toFixed(1)}%</td>
                </tr>
              ))}
              <tr className="fw-bold">
                <td>Total</td>
                <td>{summary.totalProjects}</td>
                <td>{formatCurrency(summary.totalValue)}</td>
                <td>{formatCurrency(summary.averageValue)}</td>
                <td>100%</td>
              </tr>
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
    
    const renderDetailsTable = () => (
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Project Details</h5>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                style={{ width: '200px' }}
              />
              <Button 
                variant="outline-secondary" 
                size="sm"
                title="Export as CSV"
              >
                <FaFileDownload />
              </Button>
            </div>
          </div>
          
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th onClick={() => requestSort('ProjectName')} className="sortable">
                  Project Name {getSortIcon('ProjectName')}
                </th>
                <th onClick={() => requestSort('ClientName')} className="sortable">
                  Client {getSortIcon('ClientName')}
                </th>
                <th onClick={() => requestSort('Status')} className="sortable">
                  Status {getSortIcon('Status')}
                </th>
                <th onClick={() => requestSort('CreatedAt')} className="sortable">
                  Created Date {getSortIcon('CreatedAt')}
                </th>
                <th onClick={() => requestSort('StartDate')} className="sortable">
                  Start Date {getSortIcon('StartDate')}
                </th>
                <th onClick={() => requestSort('EndDate')} className="sortable">
                  End Date {getSortIcon('EndDate')}
                </th>
                <th onClick={() => requestSort('TotalCost')} className="sortable">
                  Value {getSortIcon('TotalCost')}
                </th>
              </tr>
            </thead>
            <tbody>
              {projectDetails.map((project, index) => (
                <tr key={index}>
                  <td>{project.ProjectName}</td>
                  <td>{project.ClientName}</td>
                  <td>
                    <Badge bg={getStatusBadgeVariant(project.Status)}>
                      {project.Status}
                    </Badge>
                  </td>
                  <td>{formatDate(project.CreatedAt)}</td>
                  <td>{project.StartDate ? formatDate(project.StartDate) : 'N/A'}</td>
                  <td>{project.EndDate ? formatDate(project.EndDate) : 'N/A'}</td>
                  <td>{formatCurrency(project.TotalCost || 0)}</td>
                </tr>
              ))}
              {projectDetails.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center">No projects found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
    
    return (
      <>
        <div className="summary-cards">
          <div className="summary-card">
            <h5>Total Projects</h5>
            <h3>{summary.totalProjects}</h3>
          </div>
          <div className="summary-card">
            <h5>Total Value</h5>
            <h3>{formatCurrency(summary.totalValue)}</h3>
          </div>
          <div className="summary-card">
            <h5>Average Value</h5>
            <h3>{formatCurrency(summary.averageValue)}</h3>
          </div>
          <div className="summary-card">
            <h5>Status Categories</h5>
            <h3>{summary.statusCount}</h3>
          </div>
        </div>
        
        {(viewMode === 'chart' || viewMode === 'both') && renderCharts()}
        {(viewMode === 'table' || viewMode === 'both') && renderSummaryTable()}
        {(viewMode === 'table' || viewMode === 'both') && renderDetailsTable()}
      </>
    );
  };
  
  // Render projects by date report
  const renderProjectsByDate = () => {
    // Filter and sort projects
    let projects = reportData;
    if (searchTerm) {
      projects = getFilteredData(projects);
    }
    if (sortConfig.key) {
      projects = getSortedData(projects);
    }
    
    // Group projects by month for chart visualization
    const groupByMonth = () => {
      const months = {};
      
      projects.forEach(project => {
        const date = new Date(project.CreatedAt);
        const monthKey = `${date.getFullYear()}-${date.getMonth() + 1}`;
        
        if (!months[monthKey]) {
          months[monthKey] = {
            month: date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' }),
            count: 0,
            value: 0
          };
        }
        
        months[monthKey].count += 1;
        months[monthKey].value += project.TotalCost || 0;
      });
      
      return Object.values(months).sort((a, b) => {
        const [aYear, aMonth] = a.month.split(' ');
        const [bYear, bMonth] = b.month.split(' ');
        
        if (aYear !== bYear) {
          return aYear - bYear;
        }
        
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return monthNames.indexOf(aMonth) - monthNames.indexOf(bMonth);
      });
    };
    
    const chartData = groupByMonth();
    
    const renderCharts = () => (
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Projects by Month</h5>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" name="Number of Projects" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Project Value by Month</h5>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis 
                    tickFormatter={(value) => `$${Math.floor(value / 1000)}k`}
                  />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Value']}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="value" name="Total Value" stroke="#82ca9d" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
    
    const renderProjectsTable = () => (
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Project Details</h5>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                style={{ width: '200px' }}
              />
              <Button 
                variant="outline-secondary" 
                size="sm"
                title="Export as CSV"
              >
                <FaFileDownload />
              </Button>
            </div>
          </div>
          
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th onClick={() => requestSort('ProjectName')} className="sortable">
                  Project Name {getSortIcon('ProjectName')}
                </th>
                <th onClick={() => requestSort('ClientName')} className="sortable">
                  Client {getSortIcon('ClientName')}
                </th>
                <th onClick={() => requestSort('Status')} className="sortable">
                  Status {getSortIcon('Status')}
                </th>
                <th onClick={() => requestSort('CreatedAt')} className="sortable">
                  Created Date {getSortIcon('CreatedAt')}
                </th>
                <th onClick={() => requestSort('StartDate')} className="sortable">
                  Start Date {getSortIcon('StartDate')}
                </th>
                <th onClick={() => requestSort('EndDate')} className="sortable">
                  End Date {getSortIcon('EndDate')}
                </th>
                <th onClick={() => requestSort('PriceQuote')} className="sortable">
                  Value {getSortIcon('PriceQuote')}
                </th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project, index) => (
                <tr key={index}>
                  <td>{project.ProjectName}</td>
                  <td>{project.ClientName}</td>
                  <td>
                    <Badge bg={getStatusBadgeVariant(project.Status)}>
                      {project.Status}
                    </Badge>
                  </td>
                  <td>{formatDate(project.CreatedAt)}</td>
                  <td>{project.StartDate ? formatDate(project.StartDate) : 'N/A'}</td>
                  <td>{project.EndDate ? formatDate(project.EndDate) : 'N/A'}</td>
                  <td>{formatCurrency(project.PriceQuote || 0)}</td>
                </tr>
              ))}
              {projects.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center">No projects found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
    
    return (
      <>
        <div className="summary-cards">
          <div className="summary-card">
            <h5>Total Projects</h5>
            <h3>{summary.totalProjects}</h3>
          </div>
          <div className="summary-card">
            <h5>Total Value</h5>
            <h3>{formatCurrency(summary.totalValue)}</h3>
          </div>
          <div className="summary-card">
            <h5>Average Value</h5>
            <h3>{formatCurrency(summary.averageValue)}</h3>
          </div>
          <div className="summary-card">
            <h5>Completed Projects</h5>
            <h3>{summary.completedProjects}</h3>
          </div>
        </div>
        
        {(viewMode === 'chart' || viewMode === 'both') && renderCharts()}
        {(viewMode === 'table' || viewMode === 'both') && renderProjectsTable()}
      </>
    );
  };
  
  // Render project durations report
  const renderProjectDurations = () => {
    if (!reportData.projects || reportData.projects.length === 0) {
      return <div>No project duration data available</div>;
    }
    
    // Filter and sort projects
    let projects = reportData.projects;
    if (searchTerm) {
      projects = getFilteredData(projects);
    }
    if (sortConfig.key) {
      projects = getSortedData(projects);
    }
    
    // Prepare duration chart data
    const durationChartData = projects
      .filter(p => p.DurationDays != null)
      .sort((a, b) => a.DurationDays - b.DurationDays)
      .slice(0, 15) // Limit to top 15 for better readability
      .map(p => ({
        name: p.ProjectName.length > 20 ? p.ProjectName.substring(0, 20) + '...' : p.ProjectName,
        value: p.DurationDays || 0
      }));
    
    // Prepare status distribution chart data
    const durationByStatusData = [];
    const statusGroups = {};
    
    projects.forEach(project => {
      if (project.Status && project.DurationDays != null) {
        if (!statusGroups[project.Status]) {
          statusGroups[project.Status] = {
            count: 0,
            totalDuration: 0
          };
        }
        
        statusGroups[project.Status].count += 1;
        statusGroups[project.Status].totalDuration += project.DurationDays;
      }
    });
    
    Object.entries(statusGroups).forEach(([status, data], index) => {
      durationByStatusData.push({
        name: status,
        value: data.totalDuration / data.count, // Average duration
        color: COLORS[index % COLORS.length]
      });
    });
    
    const renderCharts = () => (
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Project Durations (Top 15)</h5>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={durationChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" width={150} />
                  <Tooltip formatter={(value) => [`${value} days`, 'Duration']} />
                  <Legend />
                  <Bar dataKey="value" name="Duration (days)" fill="#3366CC" />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Average Duration by Status</h5>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={durationByStatusData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis label={{ value: 'Days', angle: -90, position: 'insideLeft' }} />
                  <Tooltip formatter={(value) => [`${Math.round(value)} days`, 'Average Duration']} />
                  <Legend />
                  <Bar dataKey="value" name="Average Duration (days)" fill="#8884d8">
                    {durationByStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
    
    const renderDurationTable = () => (
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Project Duration Details</h5>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                style={{ width: '200px' }}
              />
              <Button 
                variant="outline-secondary" 
                size="sm"
                title="Export as CSV"
              >
                <FaFileDownload />
              </Button>
            </div>
          </div>
          
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th onClick={() => requestSort('ProjectName')} className="sortable">
                  Project Name {getSortIcon('ProjectName')}
                </th>
                <th onClick={() => requestSort('ClientName')} className="sortable">
                  Client {getSortIcon('ClientName')}
                </th>
                <th onClick={() => requestSort('Status')} className="sortable">
                  Status {getSortIcon('Status')}
                </th>
                <th onClick={() => requestSort('StartDate')} className="sortable">
                  Start Date {getSortIcon('StartDate')}
                </th>
                <th onClick={() => requestSort('EndDate')} className="sortable">
                  End Date {getSortIcon('EndDate')}
                </th>
                <th onClick={() => requestSort('DurationDays')} className="sortable">
                  Duration (days) {getSortIcon('DurationDays')}
                </th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project, index) => (
                <tr key={index}>
                  <td>{project.ProjectName}</td>
                  <td>{project.ClientName}</td>
                  <td>
                    <Badge bg={getStatusBadgeVariant(project.Status)}>
                      {project.Status}
                    </Badge>
                  </td>
                  <td>{project.StartDate ? formatDate(project.StartDate) : 'N/A'}</td>
                  <td>{project.EndDate ? formatDate(project.EndDate) : 'N/A'}</td>
                  <td>{project.DurationDays !== null ? project.DurationDays : 'N/A'}</td>
                </tr>
              ))}
              {projects.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center">No projects found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
    
    return (
      <>
        <div className="summary-cards">
          <div className="summary-card">
            <h5>Total Projects</h5>
            <h3>{summary.totalProjects}</h3>
          </div>
          <div className="summary-card">
            <h5>Average Duration</h5>
            <h3>{Math.round(summary.averageDuration)} days</h3>
          </div>
          <div className="summary-card">
            <h5>Longest Duration</h5>
            <h3>{summary.longestDuration} days</h3>
          </div>
          <div className="summary-card">
            <h5>Shortest Duration</h5>
            <h3>{summary.shortestDuration} days</h3>
          </div>
        </div>
        
        {(viewMode === 'chart' || viewMode === 'both') && renderCharts()}
        {(viewMode === 'table' || viewMode === 'both') && renderDurationTable()}
      </>
    );
  };
  
  // Render the appropriate report based on the report type
  switch (reportType) {
    case 'projects-by-status':
      return renderProjectsByStatus();
    case 'projects-by-date':
      return renderProjectsByDate();
    case 'project-durations':
      return renderProjectDurations();
    default:
      return <div>Unknown report type</div>;
  }
};

export default ProjectsReport;